import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import {
  AdminWorkspaceRoles,
  Roles,
  getRoleTranslationKey,
} from '~/data/user/role';
import {
  WorkspaceTemplate,
  WorkspaceTemplate_Empty,
} from '~/scenes/MyWorkspace/components/WorkspaceToolbar/components/WorkspaceToolbarReadMode/components/WorkspaceSetupDialog';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import {
  WorkspaceSettings,
  getRolesDefaultWidgets,
} from '~/scenes/MyWorkspace/userSettings';
import { getRoleGroups, isRoleHRAdmin } from '~/services/Permissions';

type UseDefaultWorkspaceTemplates = {
  allWorkspaceTemplates: {
    [key in AdminWorkspaceRoles]: WorkspaceSettings;
  };
  getWorkspaceTemplateByRole: (role: AdminWorkspaceRoles) => WorkspaceSettings;
  userTemplates: WorkspaceTemplate[];
};

export const useWorkspaceTemplates = (): UseDefaultWorkspaceTemplates => {
  const { t } = useTranslation();

  const roles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdmin(roles);

  const userTemplates: WorkspaceTemplate[] = useMemo(() => {
    return [WorkspaceTemplate_Empty, ...getRoleGroups(roles)];
  }, [roles]);

  const allWorkspaceTemplates = useMemo((): {
    [key in Roles]: WorkspaceSettings;
  } => {
    const addedRoles = roles.includes(Roles.EMPLOYEE)
      ? roles
      : [...roles, Roles.EMPLOYEE];
    const workspaceRoles = isHRAdmin
      ? Object.keys(AdminWorkspaceRoles)
      : addedRoles;
    return Object.fromEntries(
      Object.values(workspaceRoles).map((role): [Roles, WorkspaceSettings] => [
        role as Roles,
        {
          id: role,
          name: t(getRoleTranslationKey(role as Roles)),
          widgets: getRolesDefaultWidgets(role as Roles),
          type: WorkspaceType.Default,
          restrictToRoles: [],
        },
      ]),
    ) as { [key in Roles]: WorkspaceSettings };
  }, [t, roles, isHRAdmin]);

  const getWorkspaceTemplateByRole = useCallback(
    (role: AdminWorkspaceRoles): WorkspaceSettings => {
      return allWorkspaceTemplates[role];
    },
    [allWorkspaceTemplates],
  );

  return {
    allWorkspaceTemplates,
    getWorkspaceTemplateByRole,
    userTemplates,
  };
};
